"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var tty_utils_1 = require("./tty-utils");
/**
 * A tty is a particular device file, that sits between the shell and the terminal.
 * It acts an an interface for the shell and terminal to read/write from,
 * and communicate with one another
 */
// type AutoCompleteHandler = (index: number, tokens: string[]) => string[];
var WasmTTY = /** @class */ (function () {
    function WasmTTY(xterm) {
        this._firstInit = true;
        this.xterm = xterm;
        this._termSize = {
            cols: this.xterm.cols,
            rows: this.xterm.rows,
        };
        this._promptPrefix = "";
        this._continuationPromptPrefix = "";
        this._input = "";
        this._cursor = 0;
    }
    /**
     * Function to return a deconstructed readPromise
     */
    WasmTTY.prototype._getAsyncRead = function () {
        var _this = this;
        var readResolve;
        var readReject;
        var readPromise = new Promise(function (resolve, reject) {
            readResolve = function (response) {
                _this._promptPrefix = "";
                _this._continuationPromptPrefix = "";
                resolve(response);
            };
            readReject = reject;
        });
        return {
            promise: readPromise,
            resolve: readResolve,
            reject: readReject,
        };
    };
    /**
     * Return a promise that will resolve when the user has completed
     * typing a single line
     */
    WasmTTY.prototype.read = function (promptPrefix, continuationPromptPrefix) {
        if (continuationPromptPrefix === void 0) { continuationPromptPrefix = ""; }
        if (promptPrefix.length > 0) {
            this.print(promptPrefix);
        }
        this._firstInit = true;
        this._promptPrefix = promptPrefix;
        this._continuationPromptPrefix = continuationPromptPrefix;
        this._input = "";
        this._cursor = 0;
        return __assign({ promptPrefix: promptPrefix, continuationPromptPrefix: continuationPromptPrefix }, this._getAsyncRead());
    };
    /**
     * Return a promise that will be resolved when the user types a single
     * character.
     *
     * This can be active in addition to `.read()` and will be resolved in
     * priority before it.
     */
    WasmTTY.prototype.readChar = function (promptPrefix) {
        if (promptPrefix.length > 0) {
            this.print(promptPrefix);
        }
        return __assign({ promptPrefix: promptPrefix }, this._getAsyncRead());
    };
    /**
     * Prints a message and changes line
     */
    WasmTTY.prototype.println = function (message) {
        this.print(message + "\n");
    };
    /**
     * Prints a message and properly handles new-lines
     */
    WasmTTY.prototype.print = function (message, sync) {
        var _this = this;
        var normInput = message.replace(/[\r\n]+/g, "\n").replace(/\n/g, "\r\n");
        if (sync) {
            // We write it synchronously via hacking a bit on xterm
            //@ts-ignore
            this.xterm._core.writeSync(normInput);
            //@ts-ignore
            this.xterm._core._renderService._renderer._runOperation(function (renderer) {
                return renderer.onGridChanged(0, _this.xterm.rows - 1);
            });
        }
        else {
            //@ts-ignore
            this.xterm.write(normInput);
        }
    };
    /**
     * Prints a list of items using a wide-format
     */
    WasmTTY.prototype.printWide = function (items, padding) {
        if (padding === void 0) { padding = 2; }
        if (items.length === 0)
            return this.println("");
        // Compute item sizes and matrix row/cols
        var itemWidth = items.reduce(function (width, item) { return Math.max(width, item.length); }, 0) + padding;
        var wideCols = Math.floor(this._termSize.cols / itemWidth);
        var wideRows = Math.ceil(items.length / wideCols);
        // Print matrix
        var i = 0;
        for (var row = 0; row < wideRows; ++row) {
            var rowStr = "";
            // Prepare columns
            for (var col = 0; col < wideCols; ++col) {
                if (i < items.length) {
                    var item = items[i++];
                    item += " ".repeat(itemWidth - item.length);
                    rowStr += item;
                }
            }
            this.println(rowStr);
        }
    };
    /**
     * Prints a status message on the current line. Meant to be used with clearStatus()
     */
    WasmTTY.prototype.printStatus = function (message, sync) {
        // Save the cursor position
        this.print("\u001b[s", sync);
        this.print(message, sync);
    };
    /**
     * Clears the current status on the line, meant to be run after printStatus
     */
    WasmTTY.prototype.clearStatus = function (sync) {
        // Restore the cursor position
        this.print("\u001b[u", sync);
        // Clear from cursor to end of screen
        this.print("\u001b[1000D", sync);
        this.print("\u001b[0J", sync);
    };
    /**
     * Apply prompts to the given input
     */
    WasmTTY.prototype.applyPrompts = function (input) {
        return (this._promptPrefix +
            input.replace(/\n/g, "\n" + this._continuationPromptPrefix));
    };
    /**
     * Advances the `offset` as required in order to accompany the prompt
     * additions to the input.
     */
    WasmTTY.prototype.applyPromptOffset = function (input, offset) {
        var newInput = this.applyPrompts(input.substr(0, offset));
        return newInput.length;
    };
    /**
     * Clears the current prompt
     *
     * This function will erase all the lines that display the current prompt
     * and move the cursor in the beginning of the first line of the prompt.
     */
    WasmTTY.prototype.clearInput = function () {
        var currentPrompt = this.applyPrompts(this._input);
        // Get the overall number of lines to clear
        var allRows = (0, tty_utils_1.countLines)(currentPrompt, this._termSize.cols);
        // Get the line we are currently in
        var promptCursor = this.applyPromptOffset(this._input, this._cursor);
        var row = (0, tty_utils_1.offsetToColRow)(currentPrompt, promptCursor, this._termSize.cols).row;
        // First move on the last line
        var moveRows = allRows - row - 1;
        for (var i = 0; i < moveRows; ++i)
            this.xterm.write("\x1B[E");
        // Clear current input line(s)
        this.xterm.write("\r\x1B[K");
        for (var i = 1; i < allRows; ++i)
            this.xterm.write("\x1B[F\x1B[K");
    };
    /**
     * Clears the entire Tty
     *
     * This function will erase all the lines that display on the tty,
     * and move the cursor in the beginning of the first line of the prompt.
     */
    WasmTTY.prototype.clearTty = function () {
        // Clear the screen
        this.xterm.write("\u001b[2J");
        // Set the cursor to 0, 0
        this.xterm.write("\u001b[0;0H");
        this._cursor = 0;
    };
    /**
     * Function to return if it is the initial read
     */
    WasmTTY.prototype.getFirstInit = function () {
        return this._firstInit;
    };
    /**
     * Function to get the current Prompt prefix
     */
    WasmTTY.prototype.getPromptPrefix = function () {
        return this._promptPrefix;
    };
    /**
     * Function to get the current Continuation Prompt prefix
     */
    WasmTTY.prototype.getContinuationPromptPrefix = function () {
        return this._continuationPromptPrefix;
    };
    /**
     * Function to get the terminal size
     */
    WasmTTY.prototype.getTermSize = function () {
        return this._termSize;
    };
    /**
     * Function to get the current input in the line
     */
    WasmTTY.prototype.getInput = function () {
        return this._input;
    };
    /**
     * Function to get the current cursor
     */
    WasmTTY.prototype.getCursor = function () {
        return this._cursor;
    };
    /**
     * Function to get the size (columns and rows)
     */
    WasmTTY.prototype.getSize = function () {
        return this._termSize;
    };
    /**
     * Function to return the terminal buffer
     */
    WasmTTY.prototype.getBuffer = function () {
        return this.xterm.buffer.active;
    };
    /**
     * Replace input with the new input given
     *
     * This function clears all the lines that the current input occupies and
     * then replaces them with the new input.
     */
    WasmTTY.prototype.setInput = function (newInput, shouldNotClearInput) {
        if (shouldNotClearInput === void 0) { shouldNotClearInput = false; }
        // Doing the programming anitpattern here,
        // because defaulting to true is the opposite of what
        // not passing a param means in JS
        if (!shouldNotClearInput) {
            this.clearInput();
        }
        // Write the new input lines, including the current prompt
        var newPrompt = this.applyPrompts(newInput);
        this.print(newPrompt);
        // Trim cursor overflow
        if (this._cursor > newInput.length) {
            this._cursor = newInput.length;
        }
        // Move the cursor to the appropriate row/col
        var newCursor = this.applyPromptOffset(newInput, this._cursor);
        var newLines = (0, tty_utils_1.countLines)(newPrompt, this._termSize.cols);
        var _a = (0, tty_utils_1.offsetToColRow)(newPrompt, newCursor, this._termSize.cols), col = _a.col, row = _a.row;
        var moveUpRows = newLines - row - 1;
        this.xterm.write("\r");
        for (var i = 0; i < moveUpRows; ++i)
            this.xterm.write("\x1B[F");
        for (var i = 0; i < col; ++i)
            this.xterm.write("\x1B[C");
        // Replace input
        this._input = newInput;
    };
    /**
     * Set the new cursor position, as an offset on the input string
     *
     * This function:
     * - Calculates the previous and current
     */
    WasmTTY.prototype.setCursor = function (newCursor) {
        if (newCursor < 0)
            newCursor = 0;
        if (newCursor > this._input.length)
            newCursor = this._input.length;
        this._writeCursorPosition(newCursor);
    };
    /**
     * Sets the direct cursor value. Should only be used in keystroke contexts
     */
    WasmTTY.prototype.setCursorDirectly = function (newCursor) {
        this._writeCursorPosition(newCursor);
    };
    WasmTTY.prototype._writeCursorPosition = function (newCursor) {
        // Apply prompt formatting to get the visual status of the display
        var inputWithPrompt = this.applyPrompts(this._input);
        // Estimate previous cursor position
        var prevPromptOffset = this.applyPromptOffset(this._input, this._cursor);
        var _a = (0, tty_utils_1.offsetToColRow)(inputWithPrompt, prevPromptOffset, this._termSize.cols), prevCol = _a.col, prevRow = _a.row;
        // Estimate next cursor position
        var newPromptOffset = this.applyPromptOffset(this._input, newCursor);
        var _b = (0, tty_utils_1.offsetToColRow)(inputWithPrompt, newPromptOffset, this._termSize.cols), newCol = _b.col, newRow = _b.row;
        // Adjust vertically
        if (newRow > prevRow) {
            for (var i = prevRow; i < newRow; ++i)
                this.xterm.write("\x1B[B");
        }
        else {
            for (var i = newRow; i < prevRow; ++i)
                this.xterm.write("\x1B[A");
        }
        // Adjust horizontally
        if (newCol > prevCol) {
            for (var i = prevCol; i < newCol; ++i)
                this.xterm.write("\x1B[C");
        }
        else {
            for (var i = newCol; i < prevCol; ++i)
                this.xterm.write("\x1B[D");
        }
        // Set new offset
        this._cursor = newCursor;
    };
    WasmTTY.prototype.setTermSize = function (cols, rows) {
        this._termSize = { cols: cols, rows: rows };
    };
    WasmTTY.prototype.setFirstInit = function (value) {
        this._firstInit = value;
    };
    WasmTTY.prototype.setPromptPrefix = function (value) {
        this._promptPrefix = value;
    };
    WasmTTY.prototype.setContinuationPromptPrefix = function (value) {
        this._continuationPromptPrefix = value;
    };
    return WasmTTY;
}());
exports.default = WasmTTY;
